import { useSearchParams, Link } from 'react-router-dom';
import type { StatCardModel } from '@/types/stats';
import type { TimelineBlock } from '@/types/timeline';
import { PageHeader } from '@/components/composites/PageHeader';
import { EmployeeSwitcher } from '@/components/composites/EmployeeSwitcher';
import { StatCardGrid } from '@/components/composites/StatCardGrid';
import { DayTimeline } from '@/components/composites/DayTimeline';
import { EmptyState } from '@/components/primitives/EmptyState';
import { useAppContext } from '@/context/AppContext';
import { useEmployees } from '@/hooks/useEmployees';
import { useTimesheets } from '@/hooks/useTimesheets';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { useWorkblocksDay } from '@/hooks/useWorkblocksDay';
import { formatDayWithName, formatTimeInTz, dayOfWeekInTz, addDaysYmd } from '@/lib/dates';
import {
  buildDayBlocksFromWorkblocks,
  makeProjectColorForId,
  toTimelineBlock,
  type DayBlock,
} from '@/lib/timelineBlocks';
import styles from './TimesheetDailyPage.module.css';

/* ── Small formatting helpers (mockup path only) ───────────────────── */

function formatTime(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const period = h >= 12 ? 'PM' : 'AM';
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${displayH}:${String(m).padStart(2, '0')} ${period}`;
}

function formatDuration(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/** Map color tokens to visual border colors for block detail list. */
function borderColorFromToken(token: string): string {
  // Direct CSS custom-property identifiers produced by the live-mode
  // projectColorForId map ('--color-project-a' etc.) pass through.
  if (token.startsWith('--')) return `var(${token})`;
  // Mockup-mode fixture uses bare names like 'timeline-billable'.
  switch (token) {
    case 'timeline-billable':
      return 'var(--color-navy)';
    case 'timeline-general':
      return 'var(--color-project-b)';
    case 'timeline-idle':
      return 'var(--color-break)';
    default:
      return 'var(--color-navy)';
  }
}

function isBreakBlock(block: TimelineBlock): boolean {
  return block.colorToken === 'timeline-idle' || block.colorToken === '--color-break';
}

/* ── Stepper icons ─────────────────────────────────────────────────── */

function ChevronLeft() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 5l7 7-7 7" />
    </svg>
  );
}

/* ── Day stepper ───────────────────────────────────────────────────── */

interface DayStepperProps {
  /** Currently rendered civil date, or null when no date is selected yet. */
  currentDate: string | null;
  /** Shared query string (member=, viewStart=, viewEnd=) that every link
   *  must preserve alongside the updated `date=`. */
  searchParams: URLSearchParams;
  /** Lower bound (inclusive) — the AppContext dateRange.start. */
  lowerBound: string;
  /** Upper bound (inclusive) — the AppContext dateRange.end. */
  upperBound: string;
  /** Centre label — typically formatDayWithName(currentDate) or the
   *  range's end date when currentDate is null. */
  centreLabel: string;
}

function DayStepper({
  currentDate,
  searchParams,
  lowerBound,
  upperBound,
  centreLabel,
}: DayStepperProps) {
  const prevDate = currentDate ? addDaysYmd(currentDate, -1) : null;
  const nextDate = currentDate ? addDaysYmd(currentDate, 1) : null;

  const prevDisabled = !prevDate || prevDate < lowerBound;
  const nextDisabled = !nextDate || nextDate > upperBound;

  const buildSearch = (target: string): string => {
    const next = new URLSearchParams(searchParams);
    next.set('date', target);
    return `?${next.toString()}`;
  };

  const prevLabel = prevDate ? formatDayWithName(prevDate) : 'Previous day';
  const nextLabel = nextDate ? formatDayWithName(nextDate) : 'Next day';

  return (
    <div className={styles.stepper} data-testid="day-stepper">
      {prevDisabled || !prevDate ? (
        <span
          className={`${styles.stepperButton} ${styles.stepperButtonDisabled}`}
          aria-label={`Previous day ${prevLabel} (disabled — at start of selected range)`}
          aria-disabled="true"
          role="button"
        >
          <ChevronLeft />
          <span className={styles.stepperButtonLabel}>{prevLabel}</span>
        </span>
      ) : (
        <Link
          to={{ pathname: '/timesheet/daily', search: buildSearch(prevDate) }}
          className={styles.stepperButton}
          aria-label={`Go to previous day: ${prevLabel}`}
          data-testid="stepper-prev"
        >
          <ChevronLeft />
          <span className={styles.stepperButtonLabel}>{prevLabel}</span>
        </Link>
      )}

      <span className={styles.stepperCentre} data-testid="stepper-centre">
        {centreLabel}
      </span>

      {nextDisabled || !nextDate ? (
        <span
          className={`${styles.stepperButton} ${styles.stepperButtonDisabled}`}
          aria-label={`Next day ${nextLabel} (disabled — at end of selected range)`}
          aria-disabled="true"
          role="button"
        >
          <span className={styles.stepperButtonLabel}>{nextLabel}</span>
          <ChevronRight />
        </span>
      ) : (
        <Link
          to={{ pathname: '/timesheet/daily', search: buildSearch(nextDate) }}
          className={styles.stepperButton}
          aria-label={`Go to next day: ${nextLabel}`}
          data-testid="stepper-next"
        >
          <span className={styles.stepperButtonLabel}>{nextLabel}</span>
          <ChevronRight />
        </Link>
      )}
    </div>
  );
}

/* ── Main page ─────────────────────────────────────────────────────── */

export function TimesheetDailyPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { mockupMode, dateRange } = useAppContext();
  const { data: employees } = useEmployees();
  const { data: timesheets } = useTimesheets();
  const { data: orgSettings } = useOrgSettings();

  // Live mode has no fixture employees; fall back to the first visible
  // employee from the live roster. Mockup mode keeps the pre-task-129
  // sarah-chen default so the e2e / manual snapshot stays stable.
  const memberParam = searchParams.get('member');
  const memberFallback = mockupMode ? 'emp-sarah-chen' : (employees[0]?.id ?? null);
  const memberId = memberParam ?? memberFallback ?? null;

  const dateParam = searchParams.get('date');
  const viewStartHour = Number(searchParams.get('viewStart') ?? '8');
  const viewEndHour = Number(searchParams.get('viewEnd') ?? '18');

  // Task 152/153: orgSettings.timezone is a non-null string post-load. The
  // optional-chain default covers only the loading window; 'UTC' matches
  // the server-side default, so a flash during load reads honestly instead
  // of pretending the org is on America/New_York.
  const tz = orgSettings?.timezone ?? 'UTC';

  // Determine whether the selected date is a weekend in the org timezone.
  // We short-circuit the hook fetch in this case by passing `null` as the
  // date below — the weekend empty state renders without a network call.
  const isWeekend = (() => {
    if (!dateParam || mockupMode) return false;
    const dow = dayOfWeekInTz(dateParam, tz);
    return dow === 0 || dow === 6;
  })();

  const effectiveDate = isWeekend ? null : dateParam;

  // In mockup mode the hook returns `null` and we fall through to the
  // pre-task-129 SAMPLE_BLOCKS path below.
  const dayQuery = useWorkblocksDay(memberId, effectiveDate);
  const dayData = dayQuery.data;

  const employee = employees.find((e) => e.id === memberId) ?? employees[0];
  const bundle = employee ? timesheets[employee.id] : undefined;

  const handleMemberSwitch = (id: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('member', id);
    setSearchParams(params);
  };

  const handleRetry = () => {
    // react-query wires `queryFn` to `enabled`; the easiest "retry" is
    // simply re-setting the search params, which re-renders the hook
    // and triggers a refetch since the key is unchanged but stale.
    // A no-op params update is enough — react-router replaces the entry.
    setSearchParams(new URLSearchParams(searchParams));
  };

  /* ── Shared stepper props ─────────────────────────────────────── */

  const centreLabel = dateParam ? formatDayWithName(dateParam) : formatDayWithName(dateRange.end);
  const stepperProps: DayStepperProps = {
    currentDate: dateParam,
    searchParams,
    lowerBound: dateRange.start,
    upperBound: dateRange.end,
    centreLabel,
  };

  /* ── Mockup-mode path (byte-identical to pre-task-129) ───────── */

  if (mockupMode) {
    const blocks = bundle ? [...bundle.blocks] : [];
    const dateLabel = dateParam ? formatDayWithName(dateParam) : '';

    const workBlocks = blocks.filter((b) => !isBreakBlock(b));
    const totalWorkMin = workBlocks.reduce((s, b) => s + (b.endMin - b.startMin), 0);
    const totalWorkHours = (totalWorkMin / 60).toFixed(1);
    const sessions = workBlocks.length;
    const firstActivity = blocks.length > 0 ? formatTime(blocks[0].startMin) : '--';
    const lastActivity = blocks.length > 0 ? formatTime(blocks[blocks.length - 1].endMin) : '--';

    const cards: readonly [StatCardModel, StatCardModel, StatCardModel, StatCardModel] = [
      { label: 'Work Hours', value: `${totalWorkHours}h` },
      { label: 'Sessions', value: sessions },
      { label: 'First Activity', value: firstActivity },
      { label: 'Last Activity', value: lastActivity },
    ];

    return (
      <>
        <PageHeader
          title="Daily Timesheet"
          subtitle={employee?.name}
          actions={
            memberId ? (
              <EmployeeSwitcher currentEmployeeId={memberId} onSwitch={handleMemberSwitch} />
            ) : null
          }
        />

        {dateLabel && <div className={styles.meta}>{dateLabel}</div>}

        <StatCardGrid cards={cards} />

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Timeline</h2>
          <div className={styles.timelineCard}>
            <DayTimeline startHour={8} endHour={18} blocks={blocks} showAxis showLegend />
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Block Detail</h2>
          <div className={styles.blockList}>
            {blocks.map((block, i) => (
              <div
                key={i}
                className={styles.blockItem}
                style={{ borderLeftColor: borderColorFromToken(block.colorToken) }}
              >
                <span className={styles.blockTime}>
                  {formatTime(block.startMin)} &ndash; {formatTime(block.endMin)}
                </span>
                <span className={styles.blockDuration}>
                  {formatDuration(block.endMin - block.startMin)}
                </span>
                {isBreakBlock(block) ? (
                  <span className={styles.blockBreak}>{block.label}</span>
                ) : (
                  <div className={styles.blockInfo}>
                    <span className={styles.blockProject}>{block.label}</span>
                    {block.detail && <span className={styles.blockClient}>{block.detail}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  /* ── Live-mode path ────────────────────────────────────────────── */

  // Deterministic projectId → colour map, fresh per render — matches the
  // Range page's behaviour so the same project draws the same colour
  // when the user drills down.
  const projectColorForId = makeProjectColorForId();

  let dayBlocks: DayBlock[] = [];
  if (dayData) {
    dayBlocks = buildDayBlocksFromWorkblocks(
      dayData,
      viewStartHour,
      viewEndHour,
      tz,
      projectColorForId,
    );
  }

  // Stats — computed from dayData.summary when present, dashes otherwise.
  const workHoursValue = dayData ? `${(dayData.summary.totalWorkMinutes / 60).toFixed(1)}h` : '--';
  const sessionsValue = dayData ? dayData.summary.sessions : '--';
  const firstActivityValue = dayData?.summary.firstEvent
    ? formatTimeInTz(dayData.summary.firstEvent, tz)
    : '--';
  const lastActivityValue = dayData?.summary.lastEvent
    ? formatTimeInTz(dayData.summary.lastEvent, tz)
    : '--';

  const liveCards: readonly [StatCardModel, StatCardModel, StatCardModel, StatCardModel] = [
    { label: 'Work Hours', value: dayQuery.isLoading ? '…' : workHoursValue },
    { label: 'Sessions', value: dayQuery.isLoading ? '…' : sessionsValue },
    { label: 'First Activity', value: dayQuery.isLoading ? '…' : firstActivityValue },
    { label: 'Last Activity', value: dayQuery.isLoading ? '…' : lastActivityValue },
  ];

  // Adapt live-mode DayBlock[] to the TimelineBlock shape consumed by
  // <DayTimeline>. Detail strings stay untouched at the axis level —
  // the Block Detail list renders its own copy with eventCount info.
  const timelineBlocks: TimelineBlock[] = dayBlocks
    .filter((b) => b.widthPct > 0)
    .map((b) => toTimelineBlock(b));

  /* ── Branching render for the timeline + detail region ──────────── */

  const renderEmpty = (
    title: string,
    description?: string,
    action?: { label: string; onClick: () => void },
  ) => (
    <div className={styles.section}>
      <div className={styles.emptyStateWrap}>
        <EmptyState title={title} description={description} action={action} />
      </div>
    </div>
  );

  let bodyContent;
  if (!dateParam) {
    // D4 — pick-a-date
    bodyContent = renderEmpty(
      'Pick a date',
      'Use the stepper above or drill down from the range view.',
    );
  } else if (isWeekend) {
    // D4 — weekend
    const dow = dayOfWeekInTz(dateParam, tz);
    const dowName = dow === 6 ? 'Saturday' : 'Sunday';
    bodyContent = renderEmpty('No activity recorded', `${dowName} is outside the work week.`);
  } else if (dayQuery.isLoading) {
    // D4 — loading
    bodyContent = (
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Timeline</h2>
        <div className={styles.timelineCard}>
          <div className={styles.timelineSkeleton} aria-label="Loading day timeline" />
        </div>
      </div>
    );
  } else if (dayQuery.error) {
    // D4 — error
    bodyContent = renderEmpty(
      `Could not load activity for ${formatDayWithName(dateParam)}`,
      dayQuery.error.message,
      { label: 'Retry', onClick: handleRetry },
    );
  } else if (dayData && dayData.summary.eventCount === 0) {
    // D4 — zero events
    bodyContent = renderEmpty(`No activity on ${formatDayWithName(dateParam)}`);
  } else if (dayData) {
    // D1/D2/D5 — happy path
    bodyContent = (
      <>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Timeline</h2>
          <div className={styles.timelineCard}>
            <DayTimeline
              startHour={viewStartHour}
              endHour={viewEndHour}
              blocks={timelineBlocks}
              showAxis
              showLegend
            />
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Block Detail</h2>
          <div className={styles.blockList}>
            {dayBlocks.map((block, i) => {
              const isBreak = !block.isWork;
              const detailText = isBreak
                ? undefined
                : block.isUnclassified
                  ? `${block.eventCount} screenshots · No project assigned`
                  : `${block.eventCount} screenshots`;
              return (
                <div
                  key={i}
                  className={styles.blockItem}
                  style={{ borderLeftColor: borderColorFromToken(block.colorToken) }}
                >
                  <span className={styles.blockTime}>
                    {formatTime(block.startMin)} &ndash; {formatTime(block.endMin)}
                  </span>
                  <span className={styles.blockDuration}>
                    {formatDuration(block.endMin - block.startMin)}
                  </span>
                  {isBreak ? (
                    <span className={styles.blockBreak}>{block.label}</span>
                  ) : (
                    <div className={styles.blockInfo}>
                      <span className={styles.blockProject}>{block.label}</span>
                      {detailText && <span className={styles.blockClient}>{detailText}</span>}
                    </div>
                  )}
                  {block.isUnclassified && memberId && (
                    // TODO(task 130): wire /categorize to accept
                    // ?employeeId= and ?date= pre-fills. Until then the
                    // affordance is a noop button (dead '#' would fail
                    // jsx-a11y/anchor-is-valid); styled as an inline
                    // link so visual treatment matches the intended
                    // anchor without misleading assistive tech.
                    <button
                      type="button"
                      className={styles.categorizeLink}
                      aria-label={`Categorize block ${formatTime(block.startMin)} — ${formatTime(block.endMin)}`}
                      disabled
                      title="Pending task 130 — categorize deep-link"
                    >
                      Categorize &rarr;
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  } else {
    // Fallback — hook is disabled (no orgId yet, etc.). Render nothing
    // substantive; the stepper + stats still provide context.
    bodyContent = null;
  }

  return (
    <>
      <PageHeader
        title="Daily Timesheet"
        subtitle={employee?.name}
        actions={
          memberId ? (
            <EmployeeSwitcher currentEmployeeId={memberId} onSwitch={handleMemberSwitch} />
          ) : null
        }
      />

      <DayStepper {...stepperProps} />

      <StatCardGrid cards={liveCards} />

      {bodyContent}
    </>
  );
}
